import { useEffect, useMemo, useState } from 'react'

import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { personPalette } from '@/lib/persons'

/**
 * El mapa del dinero.
 *
 * Es la primera cosa que se ve al abrir la app y responde de un vistazo las dos
 * preguntas que importan: **de quién es** el dinero y **en qué estado está**.
 *
 * La idea que sostiene el dibujo: el tono dice de quién, el tratamiento dice
 * qué. El arco de cada persona es un bloque continuo en su color, partido en
 * tres tratamientos —pleno, pastel y rayado— que van del dinero que puede
 * gastar hoy al que ya se fue. Así se lee «cuánto de todo esto es mío» sin
 * contar rebanadas, y «cuánto de lo mío sigue libre» sin cambiar de gráfica.
 *
 *   ▰ pleno    dinero libre       — se puede gastar hoy
 *   ▨ pastel   ahorrado           — existe, pero tiene destino
 *   ▧ rayado   gastado            — ya salió; se dibuja tachado a propósito
 *
 * La deuda de tarjetas va en un aro fino por fuera, no como rebanada. Sumarla
 * al anillo inflaría el total con dinero que no se tiene: la deuda es un cerco
 * sobre lo que hay, no una parte de ello. Verla rodeando el anillo es
 * exactamente lo que significa.
 *
 * Todo se dibuja a mano en SVG. Son arcos de circunferencia y unas tramas;
 * traer una librería de gráficas para esto costaría más que el resto del
 * arranque de la app.
 */

/** Los tres estados en que puede estar el dinero de alguien. */
export type EstadoDinero = 'libre' | 'ahorrado' | 'gastado'

export interface TenenciaDinero {
  /** `null` identifica al fondo común. */
  holderId: string | null
  nombre: string
  /** Ficha de color de la persona. El fondo común no lleva. */
  colorToken: string | null
  libreCents: number
  ahorradoCents: number
  gastadoCents: number
  deudaCents: number
}

export interface SeleccionMapa {
  holderId: string | null
  estado: EstadoDinero
}

const SIZE = 216
const CENTER = SIZE / 2

/** Anillo principal: el dinero que existe. */
const R_ANILLO = 84
const S_ANILLO = 26
const C_ANILLO = 2 * Math.PI * R_ANILLO

/** Aro exterior: la deuda que lo rodea. */
const R_DEUDA = 103
const S_DEUDA = 4
const C_DEUDA = 2 * Math.PI * R_DEUDA

/**
 * Separación entre personas. Dentro de una misma no hay ninguna: eso es lo que
 * hace que su arco se lea como un bloque y no como tres rebanadas sueltas.
 *
 * Estrecha a propósito. Antes medía 14 y, con la pista asomando por detrás,
 * cada separación parecía una rebanada gris de un 3 % que nadie sabía qué era.
 * Con el fondo de la tarjeta detrás, seis unidades bastan para separar sin
 * fingir que ahí hay dinero.
 */
const HUECO = 6
/** Piso visible: sin él, un tramo real pero pequeño desaparece del anillo. */
const MIN_ARCO = 3

const ETIQUETA: Record<EstadoDinero, string> = {
  libre: 'libre',
  ahorrado: 'ahorrado',
  gastado: 'gastado',
}

export function MapaDelDinero({
  tenencias,
  formatear,
  onSeleccionar,
  className,
}: {
  tenencias: readonly TenenciaDinero[]
  /** Formateador de moneda de la app, para no duplicar reglas de es-MX aquí. */
  formatear: (cents: number) => string
  /** Se llama al tocar un tramo; `null` al deseleccionar. */
  onSeleccionar?: (seleccion: SeleccionMapa | null) => void
  className?: string
}) {
  const [dibujado, setDibujado] = useState(false)
  const [activo, setActivo] = useState<SeleccionMapa | null>(null)

  useEffect(() => {
    // Un fotograma de margen para que el navegador registre el estado inicial
    // y la animación tenga desde dónde partir.
    const id = requestAnimationFrame(() => setDibujado(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const conDinero = useMemo(
    () =>
      tenencias.filter(
        (t) => t.libreCents > 0 || t.ahorradoCents > 0 || t.gastadoCents > 0 || t.deudaCents > 0,
      ),
    [tenencias],
  )

  const total = conDinero.reduce(
    (suma, t) => suma + t.libreCents + t.ahorradoCents + t.gastadoCents,
    0,
  )
  const libreTotal = conDinero.reduce((suma, t) => suma + t.libreCents, 0)
  const deudaTotal = conDinero.reduce((suma, t) => suma + t.deudaCents, 0)

  const arcos = useMemo(() => repartir(conDinero, total), [conDinero, total])

  const seleccionar = (holderId: string | null, estado: EstadoDinero): void => {
    haptic('selection')
    const mismo = activo?.holderId === holderId && activo?.estado === estado
    const siguiente = mismo ? null : { holderId, estado }
    setActivo(siguiente)
    onSeleccionar?.(siguiente)
  }

  // En reposo el centro da el total, no el libre: el libre ya va grande en el
  // zócalo, y repetirlo aquí gastaría el hueco del anillo en un dato que la
  // pantalla acaba de dar. El total, en cambio, es lo que el anillo mide — y
  // pone en escala el trozo que sigue libre. Al tocar un tramo pasa a ese tramo.
  const tenenciaActiva = activo
    ? conDinero.find((t) => t.holderId === activo.holderId)
    : undefined
  const centroCents = tenenciaActiva ? montoDe(tenenciaActiva, activo!.estado) : total
  const centroTexto = tenenciaActiva
    ? `${tenenciaActiva.nombre} · ${ETIQUETA[activo!.estado]}`
    : `en total · ${total > 0 ? Math.round((libreTotal / total) * 100) : 0}% libre`

  // Dónde empieza y cuánto ocupa el bloque de cada persona. El aro de deuda se
  // apoya en esto para dibujarse justo encima de su dueño.
  const bloques = useMemo(() => agruparBloques(arcos), [arcos])

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className="size-full -rotate-90"
          role="img"
          aria-label={resumenAccesible(conDinero, total, deudaTotal, formatear)}
        >
          <defs>
            {conDinero.map((t, i) => (
              <Trama key={t.holderId ?? 'comun'} id={`rayado-${i}`} color={colorDe(t)} />
            ))}
          </defs>

          {/* Pista del anillo: **solo cuando no hay nada que dibujar**.
              Con datos encima, la pista asomaba por las separaciones entre
              personas y esas rendijas se leían como un tramo más —un trozo de
              dinero gris que no era de nadie y no significaba nada—. Ahora por
              la separación se ve el fondo de la tarjeta, que es lo que una
              separación tiene que enseñar. */}
          {total <= 0 ? (
            <circle
              cx={CENTER}
              cy={CENTER}
              r={R_ANILLO}
              fill="none"
              stroke="var(--surface-sunken)"
              strokeWidth={S_ANILLO}
            />
          ) : null}

          {arcos.map(({ holderId, estado, indice, largo, desde }) => {
            const tenencia = conDinero[indice]!
            const atenuado =
              activo !== null && !(activo.holderId === holderId && activo.estado === estado)
            const esActivo = !atenuado && activo !== null

            return (
              <circle
                key={`${holderId ?? 'comun'}-${estado}`}
                cx={CENTER}
                cy={CENTER}
                r={R_ANILLO}
                fill="none"
                stroke={
                  estado === 'libre'
                    ? colorDe(tenencia)
                    : estado === 'ahorrado'
                      ? pastelDe(tenencia)
                      : `url(#rayado-${indice})`
                }
                strokeWidth={esActivo ? S_ANILLO + 4 : S_ANILLO}
                strokeDasharray={dibujado ? `${largo} ${C_ANILLO - largo}` : `0 ${C_ANILLO}`}
                strokeDashoffset={-desde}
                opacity={atenuado ? 0.25 : 1}
                className="cursor-pointer"
                style={{
                  transition:
                    'stroke-dasharray 800ms cubic-bezier(0.16,1,0.3,1), opacity 200ms, stroke-width 200ms',
                }}
                onClick={() => seleccionar(holderId, estado)}
              />
            )
          })}

          {/* La deuda rodea el anillo en vez de partirlo: no es dinero que se
              tenga, es lo que ya está reclamado sobre lo que hay. Y va encima
              del bloque de quien la debe — un solo aro corrido daría a entender
              que la deuda es de quien le tocó quedar debajo. */}
          {deudaTotal > 0 ? (
            <>
              {bloques.map((b) => {
                const tenencia = conDinero[b.indice]!
                if (tenencia.deudaCents <= 0) return null
                // Se recorta al ancho del propio bloque: si se dejara crecer,
                // invadiría a la persona siguiente y le atribuiría una deuda
                // que no es suya. Un aro que cubre el bloque entero ya dice lo
                // que hay que saber: se debe más de lo que se tiene.
                const proporcional = (tenencia.deudaCents / total) * C_DEUDA
                const largo = Math.min(proporcional, b.largo * (C_DEUDA / C_ANILLO))
                return (
                  <circle
                    key={`deuda-${tenencia.holderId ?? 'comun'}`}
                    cx={CENTER}
                    cy={CENTER}
                    r={R_DEUDA}
                    fill="none"
                    stroke="var(--expense)"
                    strokeWidth={S_DEUDA}
                    strokeLinecap="round"
                    strokeDasharray={dibujado ? `${largo} ${C_DEUDA - largo}` : `0 ${C_DEUDA}`}
                    strokeDashoffset={-b.desde * (C_DEUDA / C_ANILLO)}
                    style={{ transition: 'stroke-dasharray 800ms cubic-bezier(0.16,1,0.3,1)' }}
                  />
                )
              })}
            </>
          ) : null}
        </svg>

        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-10 text-center">
          <span
            data-money
            className="font-display text-[30px] font-extrabold leading-none tracking-[-0.04em] text-ink"
          >
            {formatear(centroCents)}
          </span>
          <span className="mt-1.5 text-[11px] font-medium leading-tight text-ink-soft">
            {centroTexto}
          </span>
          {deudaTotal > 0 && !activo ? (
            <span className="mt-2 font-mono text-[10px] leading-none text-expense-text">
              {formatear(deudaTotal)} en tarjetas
            </span>
          ) : null}
        </div>
      </div>

      {/* La leyenda no es decorativa: es el control accesible. Los arcos del SVG
          no reciben foco de teclado, estos botones sí, y hacen lo mismo. */}
      <div className="mt-4 flex w-full flex-col gap-1.5">
        {conDinero.map((t) => {
          const suyo = t.libreCents + t.ahorradoCents + t.gastadoCents
          const porcentaje = total > 0 ? Math.round((suyo / total) * 100) : 0
          const activoAqui = activo?.holderId === t.holderId

          return (
            <button
              key={t.holderId ?? 'comun'}
              type="button"
              onClick={() => seleccionar(t.holderId, 'libre')}
              aria-pressed={activoAqui}
              className={cn(
                'flex min-h-[44px] w-full items-center gap-2.5 rounded-card px-2.5 py-1 text-left',
                'transition-colors duration-150 active:scale-[0.99]',
                activoAqui ? 'bg-surface-sunken' : 'bg-transparent',
              )}
            >
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ background: colorDe(t) }}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-ink">
                {t.nombre}
              </span>
              {/* El porcentaje es de todo el dinero, no solo del libre: es la
                  respuesta a «¿cuánto de esto es mío?» que pide la espec. */}
              <span className="shrink-0 font-mono text-[11px] text-ink-faint">{porcentaje}%</span>
              <span data-money className="shrink-0 text-[14px] font-semibold text-ink">
                {formatear(t.libreCents)}
              </span>
            </button>
          )
        })}
      </div>

      {/* La clave usa el color de la primera persona, no un gris: enseña el
          código de lectura con el mismo tono que se acaba de ver en el anillo,
          en vez de pedir que se traduzca de un gris neutro a ocho colores. */}
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px] text-ink-faint">
        <Clave muestra="pleno" color={colorDe(conDinero[0] ?? PLACEHOLDER)}>
          libre
        </Clave>
        <Clave muestra="pastel" color={pastelDe(conDinero[0] ?? PLACEHOLDER)}>
          ahorrado
        </Clave>
        <Clave muestra="rayado" color={colorDe(conDinero[0] ?? PLACEHOLDER)}>
          gastado
        </Clave>
        {deudaTotal > 0 ? (
          <Clave muestra="aro" color="var(--expense)">
            deuda
          </Clave>
        ) : null}
      </div>
    </div>
  )
}

/** Sirve solo para que la clave tenga un color cuando no hay dinero todavía. */
const PLACEHOLDER: TenenciaDinero = {
  holderId: null,
  nombre: '',
  colorToken: null,
  libreCents: 0,
  ahorradoCents: 0,
  gastadoCents: 0,
  deudaCents: 0,
}

function Clave({
  muestra,
  color,
  children,
}: {
  muestra: 'pleno' | 'pastel' | 'rayado' | 'aro'
  color: string
  children: string
}) {
  return (
    <span className="flex items-center gap-1.5">
      <svg width={18} height={11} viewBox="0 0 18 11" aria-hidden className="shrink-0">
        {muestra === 'aro' ? (
          <rect x={0} y={4} width={18} height={3} rx={1.5} fill={color} />
        ) : (
          <>
            <rect x={0} y={0} width={18} height={11} rx={2} fill={color} opacity={muestra === 'rayado' ? 0.2 : 1} />
            {muestra === 'rayado' ? (
              <g stroke={color} strokeWidth={2.5}>
                <path d="M-2 6 L4 0 M2 10 L10 0 M8 12 L16 2 M14 12 L20 6" />
              </g>
            ) : null}
          </>
        )}
      </svg>
      {children}
    </span>
  )
}

/**
 * Trama diagonal para el dinero ya gastado.
 *
 * Rayarlo, en vez de solo bajarle la opacidad, es lo que lo separa del ahorro
 * de un vistazo: los dos son «no disponible», pero uno sigue siendo tuyo y el
 * otro ya no está. Tacharlo dice justo eso.
 */
function Trama({ id, color }: { id: string; color: string }) {
  return (
    <pattern
      id={id}
      width={7}
      height={7}
      patternUnits="userSpaceOnUse"
      patternTransform="rotate(45)"
    >
      <rect width={7} height={7} fill={color} opacity={0.2} />
      <line x1={0} y1={0} x2={0} y2={7} stroke={color} strokeWidth={3} />
    </pattern>
  )
}

interface ArcoMapa {
  holderId: string | null
  estado: EstadoDinero
  /** Posición en la lista de tenencias, para recuperar color y trama. */
  indice: number
  largo: number
  desde: number
}

/**
 * Reparte la circunferencia.
 *
 * El hueco solo va entre personas; dentro de una persona los tres estados se
 * tocan, que es lo que hace que su arco se lea como un bloque. El descuento del
 * hueco y del arco mínimo se hace sobre los tramos grandes en vez de sumarse,
 * para que el total siga siendo exactamente la circunferencia y el anillo
 * cierre sin solaparse.
 */
function repartir(tenencias: readonly TenenciaDinero[], total: number): ArcoMapa[] {
  if (total <= 0 || tenencias.length === 0) return []

  const grupos = tenencias.length
  const hueco = grupos > 1 ? HUECO : 0
  const disponible = C_ANILLO - hueco * grupos

  const planos: Array<{ holderId: string | null; estado: EstadoDinero; indice: number; crudo: number }> =
    []
  for (const [indice, t] of tenencias.entries()) {
    for (const estado of ['libre', 'ahorrado', 'gastado'] as const) {
      const monto = montoDe(t, estado)
      if (monto > 0) {
        planos.push({ holderId: t.holderId, estado, indice, crudo: (monto / total) * disponible })
      }
    }
  }

  const conMinimo = planos.map((p) => Math.max(p.crudo, MIN_ARCO))
  const exceso = conMinimo.reduce((a, b) => a + b, 0) - disponible
  const sumaGrandes = conMinimo.filter((l) => l > MIN_ARCO).reduce((a, b) => a + b, 0)

  const finales = conMinimo.map((l) =>
    l > MIN_ARCO && sumaGrandes > 0 ? l - exceso * (l / sumaGrandes) : l,
  )

  let acumulado = 0
  let grupoPrevio = planos[0]?.indice
  return planos.map((p, i) => {
    if (p.indice !== grupoPrevio) {
      acumulado += hueco
      grupoPrevio = p.indice
    }
    const arco: ArcoMapa = {
      holderId: p.holderId,
      estado: p.estado,
      indice: p.indice,
      largo: finales[i]!,
      desde: acumulado,
    }
    acumulado += finales[i]!
    return arco
  })
}

/** Junta los tramos de cada persona en un solo arco: dónde empieza y cuánto mide. */
function agruparBloques(arcos: readonly ArcoMapa[]): Array<{
  indice: number
  desde: number
  largo: number
}> {
  const bloques: Array<{ indice: number; desde: number; largo: number }> = []
  for (const arco of arcos) {
    const ultimo = bloques[bloques.length - 1]
    if (ultimo && ultimo.indice === arco.indice) {
      ultimo.largo += arco.largo
    } else {
      bloques.push({ indice: arco.indice, desde: arco.desde, largo: arco.largo })
    }
  }
  return bloques
}

function montoDe(t: TenenciaDinero, estado: EstadoDinero): number {
  if (estado === 'libre') return t.libreCents
  if (estado === 'ahorrado') return t.ahorradoCents
  return t.gastadoCents
}

/** El fondo común no es de nadie, así que toma el azul de la marca. */
function colorDe(t: TenenciaDinero): string {
  return t.colorToken ? personPalette(t.colorToken).cssVar : 'var(--primary)'
}

function pastelDe(t: TenenciaDinero): string {
  if (t.colorToken) return personPalette(t.colorToken).pastelVar
  // `--primary-soft` es fondo de etiqueta: casi blanco, y como tramo del anillo
  // desaparecía contra la pista. Se mezcla al vuelo para obtener un índigo
  // intermedio que funcione en los dos temas sin una ficha más.
  return 'color-mix(in srgb, var(--primary) 45%, var(--surface))'
}

function resumenAccesible(
  tenencias: readonly TenenciaDinero[],
  total: number,
  deuda: number,
  formatear: (cents: number) => string,
): string {
  if (total === 0) return 'Todavía no hay dinero registrado'
  const partes = tenencias.map(
    (t) =>
      `${t.nombre}: ${formatear(t.libreCents)} libre, ${formatear(t.ahorradoCents)} ahorrado, ` +
      `${formatear(t.gastadoCents)} gastado`,
  )
  if (deuda > 0) partes.push(`Deuda en tarjetas: ${formatear(deuda)}`)
  return partes.join('. ')
}
